const PREVIEW_ROW_LIMIT = 100;
/** Matches a trailing `LIMIT <n>` clause (optionally followed by `;`) at the end of executable SQL. */
const LIMIT_CLAUSE = /\bLIMIT\s+\d+\s*;?\s*$/i;
/**
 * Walks SQL once, classifying each character as code, string literal, or comment.
 *
 * Recognises every comment style accepted by the preview backends (Presto/Trino and StarRocks):
 * `-- line`, `# line` (StarRocks/MySQL), and `/* block *​/`. Quote-aware so comment markers inside
 * single-quoted strings, double-quoted identifiers, and backtick identifiers are treated as code.
 */
const scanSql = (sql) => {
    let projection = '';
    let lastCodeIdx = -1;
    let prevCodeIdx = -1;
    const markCode = (idx) => {
        prevCodeIdx = lastCodeIdx;
        lastCodeIdx = idx;
    };
    let i = 0;
    while (i < sql.length) {
        const ch = sql[i];
        const next = sql[i + 1];
        // Line comments: -- (all engines) and # (StarRocks/MySQL). Run to end of line.
        if ((ch === '-' && next === '-') || ch === '#') {
            while (i < sql.length && sql[i] !== '\n') {
                i++;
            }
            projection += ' ';
            continue;
        }
        // Block comment: /* ... */ (non-nesting). Tolerates an unterminated block at EOF.
        if (ch === '/' && next === '*') {
            i += 2;
            while (i < sql.length && !(sql[i] === '*' && sql[i + 1] === '/')) {
                i++;
            }
            i += 2;
            projection += ' ';
            continue;
        }
        // String / identifier literals: ' " ` — all of whose contents count as code.
        if (ch === "'" || ch === '"' || ch === '`') {
            const quote = ch;
            const backslashEscapes = quote !== '`'; // StarRocks/MySQL allow \' and \"; backticks only double.
            projection += ch;
            markCode(i);
            i++;
            while (i < sql.length) {
                const c = sql[i];
                if (backslashEscapes && c === '\\' && i + 1 < sql.length) {
                    projection += c + sql[i + 1];
                    markCode(i);
                    markCode(i + 1);
                    i += 2;
                    continue;
                }
                if (c === quote) {
                    // A doubled quote ('' "" ``) is an escaped quote, not the close.
                    if (sql[i + 1] === quote) {
                        projection += c + c;
                        markCode(i);
                        markCode(i + 1);
                        i += 2;
                        continue;
                    }
                    projection += c;
                    markCode(i);
                    i++;
                    break;
                }
                projection += c;
                markCode(i);
                i++;
            }
            continue;
        }
        // Plain code.
        projection += ch;
        if (ch.trim() !== '') {
            markCode(i);
        }
        i++;
    }
    return { projection, lastCodeIdx, prevCodeIdx };
};
/**
 * Appends `LIMIT 100` to SQL preview/run requests when the executable query has no trailing LIMIT.
 *
 * The cap is inserted at the end of the executable statement — before any trailing comments
 * (`--`, `#`, `/* *​/`) and before a trailing `;` — so it is never swallowed by a comment (SSP-4042).
 * Comment markers inside string/identifier literals are ignored.
 */
export const appendPreviewLimitIfMissing = (sql, limit = PREVIEW_ROW_LIMIT) => {
    const trimmed = sql.trim();
    if (!trimmed) {
        return '';
    }
    const { projection, lastCodeIdx, prevCodeIdx } = scanSql(trimmed);
    // Nothing executable (comment-only / blank) — leave it for the backend to reject as-is.
    if (lastCodeIdx === -1) {
        return trimmed;
    }
    if (LIMIT_CLAUSE.test(projection.trimEnd())) {
        return trimmed;
    }
    // Insert before a trailing statement terminator so `LIMIT` stays inside the statement.
    const endsWithSemicolon = trimmed[lastCodeIdx] === ';';
    const insertAt = endsWithSemicolon ? (prevCodeIdx === -1 ? lastCodeIdx : prevCodeIdx + 1) : lastCodeIdx + 1;
    return `${trimmed.slice(0, insertAt)} LIMIT ${limit}${trimmed.slice(insertAt)}`;
};
